// src/RangeMeasure.js
import React, { useEffect, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import StraightenIcon from "@mui/icons-material/Straighten";

const DPR_CAP = 1.5;
const WEAK_MAGNET_PX = 18;
const HANDLE_RADIUS = 6;
const HANDLE_HIT_RADIUS = 12;
const MIN_DRAG_PX = 3;
const TOOLBAR_STYLE = {
  position: "fixed",
  left: 16,
  bottom: 30,
  zIndex: 2147483647,
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 8px",
  borderRadius: 8,
  background: "rgba(19, 23, 34, 0.88)",
  border: "1px solid rgba(255, 255, 255, 0.14)",
  boxShadow: "0 8px 22px rgba(0, 0, 0, 0.28)",
  backdropFilter: "blur(6px)",
};
const SELECT_STYLE = {
  height: 34,
  color: "#d1d4dc",
  background: "#1e222d",
  border: "1px solid rgba(255, 255, 255, 0.16)",
  borderRadius: 6,
  padding: "0 8px",
  fontFamily: "monospace",
  fontSize: 12,
  outline: "none",
};
const FIELD_NAMES = ["open", "high", "low", "close"];

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const isFiniteNumber = (value) => typeof value === "number" && Number.isFinite(value);

const normalizeTime = (time) => {
  if (time == null) return null;
  if (typeof time === "number") return Math.floor(time);
  if (typeof time === "object" && isFiniteNumber(time.timestamp)) return Math.floor(time.timestamp);
  const parsed = Date.parse(String(time));
  return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : null;
};

const findClosestIndex = (arr, time, lastIndexRef) => {
  if (!Array.isArray(arr) || arr.length === 0 || time == null) return -1;

  const n = arr.length;
  const last = isFiniteNumber(lastIndexRef.current) ? lastIndexRef.current : -1;
  if (last >= 0 && last < n) {
    const lastTime = normalizeTime(arr[last].time);
    if (lastTime === time) return last;

    let i = last;
    let steps = 0;
    if (lastTime != null && time > lastTime) {
      while (i + 1 < n && normalizeTime(arr[i + 1].time) <= time && steps < 256) {
        i += 1;
        steps += 1;
      }
    } else if (lastTime != null) {
      while (i - 1 >= 0 && normalizeTime(arr[i - 1].time) >= time && steps < 256) {
        i -= 1;
        steps += 1;
      }
    }

    if (steps < 256) {
      const left = Math.max(0, Math.min(n - 1, i));
      const right = Math.max(0, Math.min(n - 1, i + (time > lastTime ? 1 : -1)));
      const leftTime = normalizeTime(arr[left].time);
      const rightTime = normalizeTime(arr[right].time);
      if (leftTime != null && rightTime != null) {
        return Math.abs(leftTime - time) <= Math.abs(rightTime - time) ? left : right;
      }
    }
  }

  let lo = 0;
  let hi = n - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const midTime = normalizeTime(arr[mid].time);
    if (midTime === time) return mid;
    if (midTime == null || midTime < time) lo = mid + 1;
    else hi = mid - 1;
  }

  const right = clamp(lo, 0, n - 1);
  const left = clamp(lo - 1, 0, n - 1);
  const leftTime = normalizeTime(arr[left].time);
  const rightTime = normalizeTime(arr[right].time);
  if (leftTime == null) return right;
  if (rightTime == null) return left;
  return Math.abs(leftTime - time) <= Math.abs(rightTime - time) ? left : right;
};

const makeId = () => `measure-${Date.now()}-${Math.round(Math.random() * 1000000)}`;

const formatPrice = (value) => {
  if (!isFiniteNumber(value)) return "--";
  if (Math.abs(value) >= 1000) return value.toFixed(0);
  if (Math.abs(value) >= 10) return value.toFixed(2);
  return value.toFixed(4);
};

const formatDuration = (seconds) => {
  const abs = Math.abs(Math.round(seconds || 0));
  const days = Math.floor(abs / 86400);
  const hours = Math.floor((abs % 86400) / 3600);
  const minutes = Math.floor((abs % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const formatAnchorLabel = (anchor) => {
  const source = anchor?.field || "price";
  return `${source} ${formatPrice(anchor?.price)}`;
};

const getCanvasMetrics = (root) => {
  const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
  return {
    dpr,
    w: Math.max(1, Math.round(root.clientWidth)),
    h: Math.max(1, Math.round(root.clientHeight)),
  };
};

const applyCanvasSize = (canvas, root, sizeRef) => {
  const { dpr, w, h } = getCanvasMetrics(root);
  const last = sizeRef.current;
  if (last.w !== w || last.h !== h || last.dpr !== dpr) {
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    sizeRef.current = { w, h, dpr };
  }

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { ctx, w, h, dpr };
};

const drawRoundRect = (ctx, x, y, w, h, r) => {
  const radius = Math.min(r, Math.abs(w) / 2, Math.abs(h) / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

export default function RangeMeasure({
  chart,
  series,
  containerRef,
  dataRef,
  weakMagnetPx = WEAK_MAGNET_PX,
}) {
  const [active, setActive] = useState(false);
  const [shiftArmed, setShiftArmed] = useState(false);
  const [magnetMode, setMagnetMode] = useState("strong");

  const activeRef = useRef(false);
  const shiftArmedRef = useRef(false);
  const magnetModeRef = useRef("strong");
  const ctrlMagnetBypassRef = useRef(false);
  const canvasRef = useRef(null);
  const sizeRef = useRef({ w: 0, h: 0, dpr: 0 });
  const rafRef = useRef(null);
  const destroyedRef = useRef(false);
  const lastIndexRef = useRef(-1);
  const measurementsRef = useRef([]);
  const draftRef = useRef(null);
  const interactionRef = useRef(null);
  const priceScaleDragRef = useRef(null);
  const hoverSnapRef = useRef(null);
  const selectedRef = useRef({ id: null, anchor: null });

  const clearTransient = () => {
    draftRef.current = null;
    interactionRef.current = null;
    priceScaleDragRef.current = null;
    hoverSnapRef.current = null;
    selectedRef.current = { id: null, anchor: null };
  };

  const clearMeasurements = () => {
    clearTransient();
    measurementsRef.current = [];
    scheduleDraw();
  };

  const finishDraft = (endAnchor) => {
    const draft = draftRef.current;
    if (!draft || !endAnchor) return;

    const completed = {
      id: draft.id,
      start: draft.start,
      end: endAnchor,
    };
    measurementsRef.current = measurementsRef.current.concat(completed);
    draftRef.current = null;
    interactionRef.current = null;
    priceScaleDragRef.current = null;
    hoverSnapRef.current = null;
    selectedRef.current = { id: completed.id, anchor: "end" };
    setActive(false);
    scheduleDraw();
  };

  useEffect(() => {
    activeRef.current = active;
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.style.pointerEvents = "none";
    }
    scheduleDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    shiftArmedRef.current = shiftArmed;
    scheduleDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftArmed]);

  useEffect(() => {
    magnetModeRef.current = magnetMode;
    hoverSnapRef.current = null;
    scheduleDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [magnetMode]);

  const getLocalPoint = (ev) => {
    const root = containerRef?.current;
    if (!root) return null;
    const rect = root.getBoundingClientRect();
    return {
      x: ev.clientX - rect.left,
      y: ev.clientY - rect.top,
    };
  };

  const getEffectiveMagnetMode = () => {
    const mode = magnetModeRef.current;
    return mode === "strong" && ctrlMagnetBypassRef.current ? "off" : mode;
  };

  const syncCtrlMagnetBypass = (ev) => {
    const next = magnetModeRef.current === "strong" && Boolean(ev?.ctrlKey);
    if (ctrlMagnetBypassRef.current === next) return;
    ctrlMagnetBypassRef.current = next;
    hoverSnapRef.current = null;
    scheduleDraw();
  };

  const getRightPriceScaleWidth = () => {
    try {
      const width = chart?.priceScale?.("right")?.width?.();
      return isFiniteNumber(width) ? width : 0;
    } catch {
      return 0;
    }
  };

  const isRightPriceScalePointer = (ev) => {
    const root = containerRef?.current;
    if (!root || !ev) return false;

    const scaleWidth = getRightPriceScaleWidth();
    if (scaleWidth <= 0) return false;

    const rect = root.getBoundingClientRect();
    const localX = ev.clientX - rect.left;
    return localX >= rect.width - scaleWidth - 2 && localX <= rect.width + 2;
  };

  const getNearestCandle = (x) => {
    if (!chart || !dataRef?.current?.length) return null;
    const timeScale = chart.timeScale();
    let cursorTime = null;
    try {
      cursorTime = normalizeTime(timeScale.coordinateToTime(x));
    } catch {
      cursorTime = null;
    }
    if (cursorTime == null) return null;

    const arr = dataRef.current;
    const index = findClosestIndex(arr, cursorTime, lastIndexRef);
    if (index < 0 || index >= arr.length) return null;
    const candle = arr[index];
    if (!candle) return null;
    lastIndexRef.current = index;
    return { candle, index };
  };

  const getSnapCandidate = (point) => {
    if (!chart || !series || !point) return null;
    const nearest = getNearestCandle(point.x);
    if (!nearest) return null;

    let candleX = null;
    try {
      candleX = chart.timeScale().timeToCoordinate(nearest.candle.time);
    } catch {
      candleX = null;
    }
    if (!isFiniteNumber(candleX)) return null;

    let best = null;
    for (let i = 0; i < FIELD_NAMES.length; i += 1) {
      const field = FIELD_NAMES[i];
      const price = Number(nearest.candle[field]);
      if (!Number.isFinite(price)) continue;

      let y = null;
      try {
        y = series.priceToCoordinate(price);
      } catch {
        y = null;
      }
      if (!isFiniteNumber(y)) continue;

      const dx = point.x - candleX;
      const dy = point.y - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (!best || distance < best.distance) {
        best = {
          time: nearest.candle.time,
          price,
          index: nearest.index,
          field,
          x: candleX,
          y,
          distance,
          candle: nearest.candle,
        };
      }
    }
    return best;
  };

  const makeAnchor = (point) => {
    if (!chart || !series || !point) return null;

    const mode = getEffectiveMagnetMode();
    if (mode !== "off") {
      const snap = getSnapCandidate(point);
      if (snap && (mode === "strong" || (mode === "weak" && snap.distance <= weakMagnetPx))) {
        return {
          time: snap.time,
          price: snap.price,
          index: snap.index,
          snapped: true,
          field: snap.field,
        };
      }
    }

    let time = null;
    let price = null;
    try {
      time = chart.timeScale().coordinateToTime(point.x);
      price = series.coordinateToPrice(point.y);
    } catch {
      return null;
    }
    if (time == null || !isFiniteNumber(price)) return null;

    const normalized = normalizeTime(time);
    const arr = dataRef?.current || [];
    const index = normalized == null ? -1 : findClosestIndex(arr, normalized, lastIndexRef);
    if (index >= 0) lastIndexRef.current = index;

    return {
      time,
      price,
      index,
      snapped: false,
      field: null,
    };
  };

  const anchorToPoint = (anchor) => {
    if (!chart || !series || !anchor) return null;
    let x = null;
    let y = null;
    try {
      x = chart.timeScale().timeToCoordinate(anchor.time);
      y = series.priceToCoordinate(anchor.price);
    } catch {
      return null;
    }
    if (!isFiniteNumber(x) || !isFiniteNumber(y)) return null;
    return { x, y };
  };

  const scheduleDraw = () => {
    if (destroyedRef.current || rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      drawAll();
    });
  };

  const hitTestHandles = (point) => {
    if (!point) return null;
    const measurements = measurementsRef.current;
    for (let i = measurements.length - 1; i >= 0; i -= 1) {
      const measurement = measurements[i];
      const startPoint = anchorToPoint(measurement.start);
      const endPoint = anchorToPoint(measurement.end);
      const handles = [
        { name: "start", point: startPoint },
        { name: "end", point: endPoint },
      ];

      for (let h = 0; h < handles.length; h += 1) {
        const handle = handles[h];
        if (!handle.point) continue;
        const dx = point.x - handle.point.x;
        const dy = point.y - handle.point.y;
        if (Math.sqrt(dx * dx + dy * dy) <= HANDLE_HIT_RADIUS) {
          return { index: i, anchor: handle.name, measurement };
        }
      }
    }
    return null;
  };

  const getCandleCount = (start, end) => {
    const arr = dataRef?.current || [];
    if (!arr.length) return 0;

    let startIndex = isFiniteNumber(start.index) && start.index >= 0 ? start.index : -1;
    let endIndex = isFiniteNumber(end.index) && end.index >= 0 ? end.index : -1;
    if (startIndex < 0) {
      const t = normalizeTime(start.time);
      startIndex = findClosestIndex(arr, t, lastIndexRef);
    }
    if (endIndex < 0) {
      const t = normalizeTime(end.time);
      endIndex = findClosestIndex(arr, t, lastIndexRef);
    }
    if (startIndex < 0 || endIndex < 0) return 0;
    return Math.abs(endIndex - startIndex) + 1;
  };

  const getDurationSeconds = (start, end) => {
    const t1 = normalizeTime(start.time);
    const t2 = normalizeTime(end.time);
    if (t1 == null || t2 == null) return 0;
    return t2 - t1;
  };

  const drawHandle = (ctx, x, y, color, selected) => {
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, selected ? HANDLE_RADIUS + 2 : HANDLE_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = "#131722";
    ctx.fill();
    ctx.lineWidth = selected ? 3 : 2;
    ctx.strokeStyle = color;
    ctx.stroke();
    ctx.restore();
  };

  const drawLabel = (ctx, textLines, x, y, color, w, h) => {
    ctx.save();
    ctx.font = "12px monospace";
    const paddingX = 8;
    const paddingY = 6;
    let labelW = 0;
    for (let i = 0; i < textLines.length; i += 1) {
      labelW = Math.max(labelW, ctx.measureText(textLines[i]).width);
    }
    labelW += paddingX * 2;
    const labelH = textLines.length * 15 + paddingY * 2 - 2;
    const left = clamp(x, 6, Math.max(6, w - labelW - 6));
    const top = clamp(y, 6, Math.max(6, h - labelH - 6));

    drawRoundRect(ctx, left, top, labelW, labelH, 5);
    ctx.fillStyle = "rgba(19, 23, 34, 0.94)";
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = color;
    ctx.stroke();

    ctx.fillStyle = "#d1d4dc";
    ctx.textBaseline = "top";
    for (let i = 0; i < textLines.length; i += 1) {
      ctx.fillText(textLines[i], left + paddingX, top + paddingY + i * 15);
    }
    ctx.restore();
  };

  const drawSnapPreview = (ctx) => {
    const snap = hoverSnapRef.current;
    if ((!activeRef.current && !shiftArmedRef.current) || !snap) return;

    const mode = getEffectiveMagnetMode();
    const eligible = mode === "strong" || (mode === "weak" && snap.distance <= weakMagnetPx);
    const color = eligible ? "rgba(41, 187, 137, 0.95)" : "rgba(209, 212, 220, 0.55)";

    ctx.save();
    ctx.beginPath();
    ctx.arc(snap.x, snap.y, 5, 0, Math.PI * 2);
    ctx.fillStyle = "#131722";
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = color;
    ctx.stroke();
    ctx.font = "11px monospace";
    ctx.textBaseline = "middle";
    ctx.fillStyle = color;
    ctx.fillText(`${snap.field} ${formatPrice(snap.price)}`, snap.x + 9, snap.y);
    ctx.restore();
  };

  const drawMeasurement = (ctx, measurement, w, h, isDraft) => {
    const startPoint = anchorToPoint(measurement.start);
    const endPoint = anchorToPoint(measurement.end);
    if (!startPoint || !endPoint) return;

    const x1 = startPoint.x;
    const y1 = startPoint.y;
    const x2 = endPoint.x;
    const y2 = endPoint.y;
    const bullish = measurement.end.price >= measurement.start.price;
    const color = bullish ? "rgba(8, 153, 129, 0.95)" : "rgba(242, 54, 69, 0.95)";
    const fill = bullish ? "rgba(8, 153, 129, 0.14)" : "rgba(242, 54, 69, 0.14)";
    const muted = bullish ? "rgba(8, 153, 129, 0.38)" : "rgba(242, 54, 69, 0.38)";
    const left = Math.min(x1, x2);
    const top = Math.min(y1, y2);
    const rectW = Math.abs(x2 - x1);
    const rectH = Math.abs(y2 - y1);
    const selected = selectedRef.current.id === measurement.id;

    ctx.save();
    ctx.fillStyle = fill;
    ctx.strokeStyle = selected || isDraft ? color : muted;
    ctx.lineWidth = selected || isDraft ? 1.5 : 1;
    ctx.setLineDash([]);
    ctx.fillRect(left, top, rectW, rectH);
    ctx.strokeRect(left, top, rectW, rectH);

    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 4]);
    ctx.beginPath();
    ctx.moveTo(x2, y1);
    ctx.lineTo(x2, y2);
    ctx.moveTo(x1, y2);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();

    drawHandle(ctx, x1, y1, color, selected && selectedRef.current.anchor === "start");
    drawHandle(ctx, x2, y2, color, selected && selectedRef.current.anchor === "end");

    const priceDiff = measurement.end.price - measurement.start.price;
    const pct = measurement.start.price === 0 ? 0 : (priceDiff / measurement.start.price) * 100;
    const candles = getCandleCount(measurement.start, measurement.end);
    const duration = formatDuration(getDurationSeconds(measurement.start, measurement.end));
    const sign = priceDiff >= 0 ? "+" : "";
    const textLines = [
      `${sign}${formatPrice(priceDiff)} (${sign}${pct.toFixed(5)}%)`,
      `${candles} candles, ${duration}`,
      `${formatAnchorLabel(measurement.start)} -> ${formatAnchorLabel(measurement.end)}`,
    ];
    const labelX = x2 + (x2 >= x1 ? 10 : -160);
    const labelY = y2 + (y2 >= y1 ? 10 : -80);
    drawLabel(ctx, textLines, labelX, labelY, color, w, h);
  };

  const drawAll = () => {
    const canvas = canvasRef.current;
    const root = containerRef?.current;
    if (!canvas || !root) return;

    const ready = applyCanvasSize(canvas, root, sizeRef);
    if (!ready) return;
    const { ctx, w, h } = ready;
    ctx.clearRect(0, 0, w, h);

    const measurements = measurementsRef.current;
    for (let i = 0; i < measurements.length; i += 1) {
      drawMeasurement(ctx, measurements[i], w, h, false);
    }

    if (draftRef.current) {
      drawMeasurement(ctx, draftRef.current, w, h, true);
    }

    drawSnapPreview(ctx);
  };

  useEffect(() => {
    const root = containerRef?.current;
    if (!root || !chart || !series) return undefined;

    destroyedRef.current = false;
    try {
      if (window.getComputedStyle(root).position === "static") root.style.position = "relative";
    } catch {}

    const canvas = document.createElement("canvas");
    Object.assign(canvas.style, {
      position: "absolute",
      left: "0px",
      top: "0px",
      pointerEvents: "none",
      zIndex: "2147483645",
    });
    root.appendChild(canvas);
    canvasRef.current = canvas;
    applyCanvasSize(canvas, root, sizeRef);

    const redraw = () => scheduleDraw();
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            sizeRef.current = { w: 0, h: 0, dpr: 0 };
            scheduleDraw();
          })
        : null;

    if (resizeObserver) resizeObserver.observe(root);
    window.addEventListener("resize", redraw);
    try {
      chart.timeScale().subscribeVisibleLogicalRangeChange(redraw);
    } catch {}
    let inactiveClearStart = null;
    const clearOnInactiveChartPointerDown = (ev) => {
      if (ev.button !== 0 || isRightPriceScalePointer(ev) || activeRef.current || draftRef.current || measurementsRef.current.length === 0) {
        inactiveClearStart = null;
        return;
      }
      inactiveClearStart = { pointerId: ev.pointerId, x: ev.clientX, y: ev.clientY };
    };
    const clearOnInactiveChartPointerUp = (ev) => {
      const start = inactiveClearStart;
      inactiveClearStart = null;
      if (!start || start.pointerId !== ev.pointerId || activeRef.current || draftRef.current || measurementsRef.current.length === 0) {
        return;
      }
      const dx = ev.clientX - start.x;
      const dy = ev.clientY - start.y;
      if (Math.sqrt(dx * dx + dy * dy) < MIN_DRAG_PX) {
        clearMeasurements();
      }
    };
    root.addEventListener("pointerdown", clearOnInactiveChartPointerDown, true);
    root.addEventListener("pointerup", clearOnInactiveChartPointerUp, true);
    root.addEventListener("pointercancel", clearOnInactiveChartPointerUp, true);

    return () => {
      destroyedRef.current = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      window.removeEventListener("resize", redraw);
      try {
        chart.timeScale().unsubscribeVisibleLogicalRangeChange(redraw);
      } catch {}
      root.removeEventListener("pointerdown", clearOnInactiveChartPointerDown, true);
      root.removeEventListener("pointerup", clearOnInactiveChartPointerUp, true);
      root.removeEventListener("pointercancel", clearOnInactiveChartPointerUp, true);
      try {
        resizeObserver?.disconnect();
      } catch {}
      try {
        canvas.remove();
      } catch {}
      canvasRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, series, containerRef]);

  useEffect(() => {
    const root = containerRef?.current;
    if (!root || !chart || !series) return undefined;

    const updateHover = (point) => {
      hoverSnapRef.current = getEffectiveMagnetMode() === "off" ? null : getSnapCandidate(point);
      scheduleDraw();
    };

    const onPointerDown = (ev) => {
      if (ev.button !== 0) return;
      syncCtrlMagnetBypass(ev);

      if (isRightPriceScalePointer(ev)) {
        priceScaleDragRef.current = { pointerId: ev.pointerId };
        hoverSnapRef.current = null;
        scheduleDraw();
        return;
      }
      priceScaleDragRef.current = null;

      const point = getLocalPoint(ev);
      if (!point) return;

      const canStart = activeRef.current || draftRef.current || ev.shiftKey;
      if (!canStart) return;

      const hit = hitTestHandles(point);
      if (hit) {
        ev.preventDefault();
        ev.stopPropagation();
        selectedRef.current = { id: hit.measurement.id, anchor: hit.anchor };
        interactionRef.current = {
          type: "edit",
          pointerId: ev.pointerId,
          measurementIndex: hit.index,
          anchor: hit.anchor,
        };
        setActive(true);
        try {
          root.setPointerCapture(ev.pointerId);
        } catch {}
        updateHover(point);
        return;
      }

      const anchor = makeAnchor(point);
      if (!anchor) return;

      if (draftRef.current) {
        ev.preventDefault();
        ev.stopPropagation();
        finishDraft(anchor);
        return;
      }

      ev.preventDefault();
      ev.stopPropagation();
      const draft = {
        id: makeId(),
        start: anchor,
        end: anchor,
      };
      draftRef.current = draft;
      setActive(true);
      try {
        root.setPointerCapture(ev.pointerId);
      } catch {}
      selectedRef.current = { id: draft.id, anchor: "start" };
      interactionRef.current = {
        type: "create",
        pointerId: ev.pointerId,
        startPoint: point,
        dragged: false,
      };
      updateHover(point);
    };

    const onPointerMove = (ev) => {
      syncCtrlMagnetBypass(ev);
      if (priceScaleDragRef.current?.pointerId === ev.pointerId) {
        if (ev.buttons === 0) {
          priceScaleDragRef.current = null;
          scheduleDraw();
        }
        return;
      }

      const point = getLocalPoint(ev);
      if (!point) return;

      if (!activeRef.current && !shiftArmedRef.current && !draftRef.current) return;

      const interaction = interactionRef.current;
      if (!interaction) {
        if (draftRef.current) {
          const anchor = makeAnchor(point);
          if (anchor) {
            draftRef.current.end = anchor;
            selectedRef.current = { id: draftRef.current.id, anchor: "end" };
          }
        }
        updateHover(point);
        return;
      }

      if (interaction.pointerId !== ev.pointerId) return;
      ev.preventDefault();
      ev.stopPropagation();
      const anchor = makeAnchor(point);
      if (!anchor) return;

      if (interaction.type === "create") {
        if (draftRef.current) {
          draftRef.current.end = anchor;
          selectedRef.current = { id: draftRef.current.id, anchor: "end" };
          const dx = point.x - interaction.startPoint.x;
          const dy = point.y - interaction.startPoint.y;
          interaction.dragged = interaction.dragged || Math.sqrt(dx * dx + dy * dy) >= MIN_DRAG_PX;
        }
      } else if (interaction.type === "edit") {
        const measurement = measurementsRef.current[interaction.measurementIndex];
        if (measurement) {
          measurement[interaction.anchor] = anchor;
          selectedRef.current = { id: measurement.id, anchor: interaction.anchor };
        }
      }

      updateHover(point);
    };

    const onPointerUp = (ev) => {
      syncCtrlMagnetBypass(ev);
      if (priceScaleDragRef.current?.pointerId === ev.pointerId) {
        priceScaleDragRef.current = null;
        scheduleDraw();
        return;
      }

      const interaction = interactionRef.current;
      if (!interaction || interaction.pointerId !== ev.pointerId) return;

      ev.preventDefault();
      try {
        root.releasePointerCapture(ev.pointerId);
      } catch {}

      if (interaction.type === "create" && draftRef.current) {
        if (interaction.dragged) {
          finishDraft(draftRef.current.end);
        }
      } else if (interaction.type === "edit") {
        setActive(false);
      }

      interactionRef.current = null;
      scheduleDraw();
    };

    const onPointerLeave = () => {
      if (!interactionRef.current) {
        hoverSnapRef.current = null;
        scheduleDraw();
      }
    };

    root.addEventListener("pointerdown", onPointerDown, true);
    root.addEventListener("pointermove", onPointerMove, true);
    root.addEventListener("pointerup", onPointerUp, true);
    root.addEventListener("pointercancel", onPointerUp, true);
    root.addEventListener("pointerleave", onPointerLeave);

    return () => {
      root.removeEventListener("pointerdown", onPointerDown, true);
      root.removeEventListener("pointermove", onPointerMove, true);
      root.removeEventListener("pointerup", onPointerUp, true);
      root.removeEventListener("pointercancel", onPointerUp, true);
      root.removeEventListener("pointerleave", onPointerLeave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, series, containerRef, dataRef, weakMagnetPx]);

  useEffect(() => {
    const onKeyDown = (ev) => {
      const targetName = ev.target?.tagName;
      const isTypingTarget = targetName === "INPUT" || targetName === "TEXTAREA" || targetName === "SELECT" || ev.target?.isContentEditable;

      if (ev.key === "Shift" && !isTypingTarget && !shiftArmedRef.current) {
        setShiftArmed(true);
      }

      if (ev.key === "Control" && !isTypingTarget) {
        syncCtrlMagnetBypass(ev);
      }

      if (ev.key === "Escape") {
        clearMeasurements();
        setActive(false);
        setShiftArmed(false);
      }
      if (activeRef.current && (ev.key === "Delete" || ev.key === "Backspace") && selectedRef.current.id) {
        const selectedId = selectedRef.current.id;
        measurementsRef.current = measurementsRef.current.filter((m) => m.id !== selectedId);
        selectedRef.current = { id: null, anchor: null };
        scheduleDraw();
      }
    };

    const onKeyUp = (ev) => {
      if (ev.key === "Shift") {
        setShiftArmed(false);
      }
      if (ev.key === "Control" && ctrlMagnetBypassRef.current) {
        ctrlMagnetBypassRef.current = false;
        hoverSnapRef.current = null;
        scheduleDraw();
      }
    };

    const onBlur = () => {
      if (!ctrlMagnetBypassRef.current) return;
      ctrlMagnetBypassRef.current = false;
      hoverSnapRef.current = null;
      scheduleDraw();
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={TOOLBAR_STYLE}>
      <Tooltip title={active ? "Measure tool on" : shiftArmed ? "Shift armed: click chart to measure" : "Measure tool off"}>
        <IconButton
          size="small"
          onClick={() => {
            draftRef.current = null;
            interactionRef.current = null;
            hoverSnapRef.current = null;
            setActive((value) => !value);
          }}
          sx={{
            width: 36,
            height: 36,
            color: active || shiftArmed ? "#fff" : "#d1d4dc",
            backgroundColor: active ? "#2962ff" : shiftArmed ? "#089981" : "rgba(255,255,255,0.06)",
            "&:hover": {
              backgroundColor: active ? "#1e53e5" : shiftArmed ? "#07856f" : "rgba(255,255,255,0.10)",
            },
          }}
        >
          <StraightenIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <select
        value={magnetMode}
        onChange={(ev) => setMagnetMode(ev.target.value)}
        style={SELECT_STYLE}
        aria-label="Measure magnet mode"
      >
        <option value="off">Magnet off</option>
        <option value="weak">Weak magnet</option>
        <option value="strong">Strong magnet</option>
      </select>
    </div>
  );
}
